import React, { useCallback, useEffect, useRef, useState } from "react";
import { useStudioPageChromeOverride } from "../../common/StudioPageChromeContext";
import HKPConfirmDialog from "../../common/Components/HKPConfirmDialog";
import notifications from "../../common/Components/Notifications/Notification";
import { getApi, postApi } from "../../helpers/auth_helpers";
import { Catalogue } from "../../types/catalogue.types";
import HKPProductInventoryPicker from "../products/HKPProductInventoryPicker";
import InventoryProductPrices from "../products/InventoryProductPrices";
import { fetchPickerProducts, PickerProduct, ProductPageError, ProductPageLoader } from "../products/productSelection";
import { catalogueAddError, catalogueAddReason, catalogueEligibilityError, parseCatalogueAddEligibility } from "./catalogueAddInventory";
import "./catalogue-manager.scss";

type Props = { catalogue: Catalogue; seedMembershipIds?: string[]; onBack: () => void; onAdded: () => void };
const server = process.env.REACT_APP_SERVERURL || "";

/** Catalogue adapter: the shared Product picker owns paging, selection, summaries and preview. */
export default function CatalogueAddFromInventory({ catalogue, onBack, onAdded }: Props) {
  const [priceFiltersEnabled, setPriceFiltersEnabled] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [confirmBack, setConfirmBack] = useState(false);
  const selected = useRef<PickerProduct[]>([]);
  const busy = useRef(false);
  useEffect(() => {
    let current = true;
    void getApi(`${server}/product/price-filter-status`).then((response: any) => {
      if (current) setPriceFiltersEnabled(response?.success === true && response?.data?.enabled === true);
    }).catch(() => { /* Fail closed on an older backend or incomplete migration. */ });
    return () => { current = false; };
  }, []);
  const requestBack = useCallback(() => {
    if (busy.current) return;
    if (selected.current.length) setConfirmBack(true); else onBack();
  }, [onBack]);
  useStudioPageChromeOverride({ title: "Add from inventory", subtitle: catalogue.name, onBack: requestBack });
  const selectionChanged = useCallback((products: PickerProduct[]) => { selected.current = products; }, []);
  const loadPage = useCallback<ProductPageLoader>(async (query, page, limit) => {
    const result = await fetchPickerProducts(query, page, limit);
    if (!result.products.length) return result;
    const ids = result.products.map((row) => row.productUniqueId).filter((id) => typeof id === "string" && id.trim());
    if (!ids.length) return { products: [], total: result.total };
    const response: any = await postApi(`${server}/catalouge/${catalogue.id}/add-eligibility`, { productIds: Array.from(new Set(ids)) });
    const eligibility = parseCatalogueAddEligibility(response, Array.from(new Set(ids)));
    if (!eligibility) {
      throw new ProductPageError(catalogueEligibilityError(response));
    }
    return { ...result, products: result.products.map((product) => ({ ...product,
      canAdd: eligibility[product.productUniqueId]?.selectable === true,
      reason: catalogueAddReason(eligibility[product.productUniqueId]?.reasonCode),
    })) };
  }, [catalogue.id]);
  const add = async (products: PickerProduct[]) => {
    busy.current = true; setFeedback("");
    try {
      const productIds = products.map((product) => product.productUniqueId);
      // Revalidates the entire selection, including rows outside this filter.
      const response: any = await postApi(`${server}/catalouge/${catalogue.id}/addProducts`, { productIds });
      if (response?.success === true) {
        notifications({ title: "Products added", message: "Selected products were added to the catalogue.", type: "success" });
        onAdded();
        return { addedIds: productIds };
      }
      setFeedback(catalogueAddError(response));
      if (!response || response.code === "NETWORK_UNAVAILABLE") throw new Error("Unconfirmed addition");
      const rejectedIds = Array.isArray(response.data?.products)
        ? response.data.products.map((row: any) => row.productUniqueId).filter((id: any) => productIds.includes(id)) : [];
      return { addedIds: [], rejectedIds };
    } finally { busy.current = false; }
  };
  return <>
    <p className="catalogue-add-from-inventory-lead">Add existing products to <strong>{catalogue.name}</strong>. Already included and unavailable products stay visible with a reason. Prices below belong to Product inventory.</p>
    <HKPProductInventoryPicker loadPage={loadPage} onAdd={add} onDone={requestBack} doneLabel="Cancel"
      onSelectionChange={selectionChanged} addedReason="Already in this catalogue." inventoryPricing priceFiltersEnabled={priceFiltersEnabled}
      renderPrices={(product, onSaved) => <InventoryProductPrices product={product} onSaved={onSaved} />}
      feedback={feedback ? <p role="alert">{feedback}</p> : undefined} />
    <HKPConfirmDialog open={confirmBack} title="Discard selected products?" description="Your selection has not been added to this catalogue. Saved Product price edits will remain saved."
      confirmLabel="Discard selection" onCancel={() => setConfirmBack(false)} onConfirm={onBack} />
  </>;
}
